import { CLOUD_API, STORAGE_API, STORAGE_API_KEY } from '../config'
import { getAccessToken } from '../auth'

/**
 * Upload ueber die cloud-api der Instanz (#53, 02.10.2026): Mandanten-Builds
 * tragen KEINEN Storage-Schluessel mehr im Browser-Bundle — er lag dort
 * oeffentlich abrufbar und gilt in der storage-api als Admin. Die cloud-api
 * nimmt die Datei mit dem Nutzer-Token an und laedt sie serverseitig hoch.
 */
export async function uploadViaCloudApi(file: File): Promise<{ id: string; url: string }> {
  if (!CLOUD_API) {
    throw new Error('Upload: weder Storage-Schluessel noch cloud-api konfiguriert')
  }
  const token = getAccessToken()
  if (!token) throw new Error('Upload: bitte anmelden')
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${CLOUD_API}/api/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  if (!res.ok) throw new Error(`Upload: HTTP ${res.status}`)
  const data = (await res.json()) as { storage_id?: string | number; url?: string; via?: string }
  if (data.via !== 'storage-api' || data.storage_id == null || !data.url) {
    throw new Error('Upload: Storage nicht erreichbar')
  }
  return { id: String(data.storage_id), url: data.url }
}

/**
 * Upload a file to the storage service. Returns the public media URL.
 *
 * For canvas-card image-drop: pass an image File from drag/drop or
 * paste. The upload is permanent (no TTL) and is_public so the URL
 * can be embedded in a paragraph as markdown `![](url)` without
 * auth concerns.
 *
 * Response shape (best-effort — storage-api may return additional
 * fields). We need `object_id` to construct the public URL.
 */
export interface UploadResult {
  object_id: string
  url: string
}

export async function uploadFile(
  file: File,
  opts: {
    isPublic?: boolean
    permanent?: boolean
    context?: string
  } = {},
): Promise<UploadResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('original_filename', file.name)
  form.append('is_public', opts.isPublic === false ? 'false' : 'true')
  if (opts.context) form.append('context', opts.context)
  // ttl_hours omitted entirely → permanent storage. (Sending '' or
  // 'null' tends to trip the multipart schema validator.)
  if (opts.permanent === false) {
    form.append('ttl_hours', '24')
  }
  // Skip AI analysis for fast user-facing drops — keeps the round-
  // trip short. AI tagging can be a Phase 5d follow-up via a separate
  // endpoint.
  form.append('analyze', 'false')

  if (!STORAGE_API_KEY) {
    const r = await uploadViaCloudApi(file)
    return { object_id: r.id, url: r.url }
  }
  if (!STORAGE_API) {
    throw new Error('uploadFile: Storage ist auf dieser Instanz nicht konfiguriert (VITE_STORAGE_API)')
  }
  const url = `${STORAGE_API}/storage/upload`
  const headers: Record<string, string> = {}
  if (STORAGE_API_KEY) headers['X-API-KEY'] = STORAGE_API_KEY

  const res = await fetch(url, { method: 'POST', body: form, headers })
  if (!res.ok) {
    throw new Error(`uploadFile: HTTP ${res.status}`)
  }
  const data = (await res.json()) as { object_id?: string; id?: string; url?: string }
  const objectId = data.object_id ?? data.id
  if (!objectId) {
    throw new Error('uploadFile: response missing object_id')
  }
  const publicUrl =
    data.url ?? `${STORAGE_API}/storage/media/${objectId}`
  return { object_id: objectId, url: publicUrl }
}
